export interface RebetDrawRef {
  draw_id?: number
  event_id?: number
  draw_date?: string
}

export interface UpcomingDrawOption {
  draw_date: string
  cutoff?: string
  event_id: number
}

function sortDrawsChronologically(draws: UpcomingDrawOption[]): UpcomingDrawOption[] {
  return [...draws].sort(
    (a, b) => Date.parse(a.draw_date) - Date.parse(b.draw_date)
  )
}

/**
 * Map a historical wager draw onto the current upcoming-draws list.
 * 1) Same open event_id (ticket still on that draw)
 * 2) Same draw time (within 1 minute)
 * 3) Original draw closed (settled/past) → earliest open draw (typical "rebet my numbers" flow)
 */
export function resolveRebetDraws(
  game: RebetDrawRef,
  availableDraws: UpcomingDrawOption[],
  options?: { closedDrawFallbackIndex?: number }
): UpcomingDrawOption[] {
  if (!availableDraws.length) return []

  const sortedOpen = sortDrawsChronologically(availableDraws)
  const targetEventId = Number(game.event_id ?? game.draw_id)
  if (Number.isFinite(targetEventId) && targetEventId > 0) {
    const byId = availableDraws.find((draw) => draw.event_id === targetEventId)
    if (byId) return [byId]
  }

  if (game.draw_date) {
    const targetMs = Date.parse(game.draw_date)
    if (!Number.isNaN(targetMs)) {
      const byDate = availableDraws.find((draw) => {
        const drawMs = Date.parse(draw.draw_date)
        return !Number.isNaN(drawMs) && Math.abs(drawMs - targetMs) < 60_000
      })
      if (byDate) return [byDate]
    }
  }

  const drawMs = game.draw_date ? Date.parse(game.draw_date) : NaN
  const originalEventStillOpen =
    Number.isFinite(targetEventId) &&
    targetEventId > 0 &&
    availableDraws.some((draw) => draw.event_id === targetEventId)
  const originalDrawIsPast =
    !originalEventStillOpen &&
    !Number.isNaN(drawMs) &&
    drawMs <= Date.now()

  if (originalDrawIsPast) {
    const fallbackIndex = options?.closedDrawFallbackIndex ?? 0
    const pick = sortedOpen[fallbackIndex] ?? sortedOpen[0]
    return pick ? [pick] : []
  }

  return []
}
